import { clearAuthStorage, getStoredToken, isDemoModeSession } from "./auth";
import { API_BASE, ERP_BASE } from "../config/apiBase";

export const BOOSTER_SESSION_TIMEOUT_NOTICE_KEY = "booster_session_timeout_notice";
const SESSION_TIMEOUT_MESSAGE = "Session expired. Please login again.";

let installed = false;
let nativeFetch = null;
const sessionConfirmations = new Map();

const isBrowser = typeof window !== "undefined";
const apiBaseOrigin = (() => {
  if (!isBrowser) return "";
  try {
    return new URL(API_BASE, window.location.origin).origin;
  } catch (error) {
    return "";
  }
})();
const erpBaseOrigin = (() => {
  if (!isBrowser) return "";
  try {
    return new URL(ERP_BASE, window.location.origin).origin;
  } catch (error) {
    return "";
  }
})();

const toUrl = (input) => {
  if (!isBrowser) return null;
  try {
    return new URL(String(input || ""), window.location.origin);
  } catch (error) {
    return null;
  }
};

const isAuthEndpoint = (pathname = "") => {
  const value = String(pathname || "").toLowerCase();
  return (
    value === "/api/auth/login" ||
    value === "/api/login" ||
    value === "/api/auth/session" ||
    value === "/api/session" ||
    value === "/login" ||
    value === "/logout"
  );
};

const isApiLikeRequest = (pathname = "") => {
  const value = String(pathname || "").toLowerCase();
  return (
    value.startsWith("/api/") ||
    value.startsWith("/word/api/") ||
    value.startsWith("/sentence/api/") ||
    value.startsWith("/shoppingcart/api/")
  );
};

const shouldForceLogoutOn401 = (pathname = "") => {
  const value = String(pathname || "").toLowerCase();
  if (isAuthEndpoint(value)) return false;
  return isApiLikeRequest(value);
};

const confirmCanonicalSession = async (token) => {
  if (!token) return "invalid";
  if (!sessionConfirmations.has(token)) {
    const confirmation = nativeFetch(`${ERP_BASE}/api/session`, {
      method: "GET",
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-store, no-cache, must-revalidate",
        Pragma: "no-cache",
      },
    })
      .then((response) => (response.status === 401 ? "invalid" : response.ok ? "valid" : "unknown"))
      .catch(() => "unknown")
      .finally(() => {
        sessionConfirmations.delete(token);
      });
    sessionConfirmations.set(token, confirmation);
  }
  return sessionConfirmations.get(token);
};

export const installAuthFetchInterceptor = () => {
  if (!isBrowser || installed) return;
  installed = true;
  nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = toUrl(input);
    const pathname = url?.pathname || "";
    const isSameOrigin =
      Boolean(url) &&
      (url.origin === window.location.origin ||
        (apiBaseOrigin && url.origin === apiBaseOrigin) ||
        (erpBaseOrigin && url.origin === erpBaseOrigin));
    const isDemoSession = isDemoModeSession();
    const token = isDemoSession ? "" : getStoredToken();

    const requestInit = { ...init };
    const headers = new Headers(init?.headers || {});

    if (isSameOrigin && isAuthEndpoint(pathname)) {
      headers.set("Cache-Control", "no-store, no-cache, must-revalidate");
      headers.set("Pragma", "no-cache");
      requestInit.cache = "no-store";
    }

    if (isSameOrigin && !isAuthEndpoint(pathname)) {
      headers.delete("x-auth-token");
      if (token) headers.set("Authorization", `Bearer ${token}`);
    }

    requestInit.headers = headers;
    const response = await nativeFetch(input, requestInit);

    if (isSameOrigin && response.status === 401 && !isDemoSession && shouldForceLogoutOn401(pathname)) {
      const confirmation = await confirmCanonicalSession(token);
      if (confirmation === "invalid" && getStoredToken() === token) {
        clearAuthStorage();
        localStorage.setItem(BOOSTER_SESSION_TIMEOUT_NOTICE_KEY, SESSION_TIMEOUT_MESSAGE);
        if (window.location.pathname !== "/") window.location.replace("/");
      }
    }

    return response;
  };
};

export const resetAuthFetchInterceptorForTests = () => {
  if (installed && nativeFetch && typeof window !== "undefined") window.fetch = nativeFetch;
  installed = false;
  nativeFetch = null;
  sessionConfirmations.clear();
};
